/**
 * Phase 20 / Task 20.7 — the pure half of step 4 (run, progress, result, undo).
 *
 * Kept apart from RunStep.tsx so the decisions — which state a family is in,
 * what the bar shows, which batch to re-attach to — are tested without
 * rendering anything.
 */

import { familyLabel } from './mapping-state';
import type {
  CommitReport,
  CommitState,
  CommitStatus,
  EntityProgress,
  FamilyReport,
  UndoBlocker,
} from './types';

/** How often a live import is polled. */
export const POLL_MS = 1500;

/** QUEUED or RUNNING: rows may still be written, so keep polling and guarding. */
export function isLive(state: CommitState | null): boolean {
  return state === 'QUEUED' || state === 'RUNNING';
}

/**
 * Null `commitState` is "never asked to commit". The wizard only shows a batch
 * here after a 202, which sets QUEUED in the same statement, so a null read is
 * the moment before that write is visible — it is waiting, not finished.
 */
export function isWaiting(status: CommitStatus): boolean {
  return status.commitState === null || isLive(status.commitState);
}

export type EntityPhase = 'done' | 'active' | 'waiting' | 'unknown';

/**
 * §80's per-family line: ✓ done, ⟳ importing (23 of 89), — waiting.
 *
 * `unknown` is a dry run, whose `done` is null by design — its outcomes are
 * uncommitted until they are discarded, so nothing outside the run can count
 * them. That is shown as "previewing", never as 0.
 */
export function entityPhase(e: EntityProgress): EntityPhase {
  if (e.done === null) return 'unknown';
  if (e.done >= e.total) return 'done';
  if (e.done > 0) return 'active';
  return 'waiting';
}

/** Whole percent, 0–100, or null when progress is indeterminate. */
export function percentDone(done: number | null, total: number): number | null {
  if (done === null) return null;
  if (total <= 0) return 0;
  return Math.min(100, Math.floor((done / total) * 100));
}

/**
 * The family's display name, with its sheet only when the same family arrived
 * on more than one sheet — otherwise "Tanks (Sheet3)" is noise.
 */
export function entityLabel(e: EntityProgress, all: readonly EntityProgress[]): string {
  const label = familyLabel(e.entityFamily);
  const repeats = all.filter((x) => x.entityFamily === e.entityFamily).length > 1;
  return repeats && e.sheetName !== null ? `${label} (${e.sheetName})` : label;
}

/**
 * The finished summary. A DONE batch always carries `report`; the per-entity
 * counts are the fallback so a missing report still shows real numbers rather
 * than an empty table.
 */
export function summaryOf(status: CommitStatus): CommitReport {
  if (status.report !== null) return status.report;
  const families: FamilyReport[] = status.progress.entities.map((e) => ({
    entityFamily: e.entityFamily,
    imported: e.created,
    updated: e.updated,
    matched: e.matched,
    pendingLink: e.pendingLink ?? 0,
    skipped: e.skipped,
    failed: e.failed,
    skippedReasons: [],
  }));
  const sum = (k: 'imported' | 'updated' | 'matched' | 'skipped' | 'failed'): number =>
    families.reduce((n, f) => n + f[k], 0);
  return {
    imported: sum('imported'),
    updated: sum('updated'),
    matched: sum('matched'),
    pendingLink: families.reduce((n, f) => n + (f.pendingLink ?? 0), 0),
    skipped: sum('skipped'),
    failed: sum('failed'),
    alreadyDone: 0,
    skippedReasons: [],
    families,
  };
}

/**
 * Whether Undo is offered. Undo removes only what a batch CREATED, so a batch
 * that created nothing has nothing to undo; a dry run wrote nothing; an undone
 * batch has been undone. A batch that only ASKED people to join (DEC-346 a) still has
 * something to undo: the open requests are withdrawn, and anyone who already accepted is
 * un-linked.
 */
export function canUndo(status: CommitStatus): boolean {
  if (status.isDryRun || status.commitState !== 'DONE' || status.status === 'UNDONE') {
    return false;
  }
  const summary = summaryOf(status);
  return summary.imported > 0 || (summary.pendingLink ?? 0) > 0;
}

/** At most this many blocker lines on screen; the rest are counted. */
export const MAX_BLOCKERS_SHOWN = 10;

/**
 * The refusal's lines. Ten records blocked by the same invoice table produce
 * ten identical messages; one is enough.
 */
export function blockerLines(blockers: readonly UndoBlocker[]): {
  readonly shown: readonly string[];
  readonly hidden: number;
} {
  const distinct = [...new Set(blockers.map((b) => b.message))];
  return {
    shown: distinct.slice(0, MAX_BLOCKERS_SHOWN),
    hidden: Math.max(0, distinct.length - MAX_BLOCKERS_SHOWN),
  };
}

// ── The remembered batch ────────────────────────────────────────────────────
//
// The import runs on the server whether or not this page stays open, so
// leaving it (after the guard's confirm, or with the browser's Back button,
// which cannot be intercepted) must not strand the hauler with no way back to
// the result. The running batch's id is kept for the tab's lifetime, and the
// wizard opens on it again.
//
// sessionStorage, not localStorage: one tab's import is that tab's. It can be
// unavailable (private mode, blocked site data), so every access is guarded and
// a failure means only that the wizard opens fresh. Nothing depends on it — a
// stale or foreign id answers 404 and is forgotten.

const RUNNING_KEY = 'lodex.import.runningBatch';

export function rememberRunningBatch(batchId: string): void {
  try {
    window.sessionStorage.setItem(RUNNING_KEY, batchId);
  } catch {
    // Storage unavailable — the wizard simply will not re-attach.
  }
}

export function recallRunningBatch(): string | null {
  try {
    return window.sessionStorage.getItem(RUNNING_KEY);
  } catch {
    return null;
  }
}

export function forgetRunningBatch(): void {
  try {
    window.sessionStorage.removeItem(RUNNING_KEY);
  } catch {
    // Nothing to forget if storage was never reachable.
  }
}
